import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Upload,
  Alert,
  Image,
  Popconfirm,
  Divider,
  Space,
} from "antd";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { countryCodeOptions } from "@shared/lib/constants";
import {
  GenderEnum,
  ICreateInfluencerRequest,
  InfluencerType,
} from "@shared/lib/types/influencer";
import styles from "./styles.module.scss";
import { PlusOutlined } from "@ant-design/icons";
import { useGetLocationFacets } from "../lib/hooks/useGetLocationFacets";
import { CircularProgress } from "@shared/ui";
import { useGetAgencies } from "@entities/agency";

const { Option } = Select;
interface Props {
  onSubmit: (values: ICreateInfluencerRequest) => void;
  isSubmitting: boolean;
  initialCategories: string[];
}

export const InfluencerForm = ({
  onSubmit,
  isSubmitting,
  initialCategories,
}: Props) => {
  const [form] = Form.useForm();
  const [newRole, setNewRole] = useState("");

  const [selectedLocation, setSelectedLocation] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
  >(undefined);

  const [searchLocation, setSearchLocation] = useState<string>("");

  const { locationFacets, isFetching } = useGetLocationFacets(searchLocation);

  const [availableCategories, setAvailableCategories] =
    useState(initialCategories);

  useEffect(() => {
    setAvailableCategories(initialCategories);
  }, [initialCategories]);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const addNewCategories = (roleName: string) => {
    if (!roleName.trim() || availableCategories.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availableCategories, roleName];
    setAvailableCategories(updatedRoles);
    setNewRole("");
    form.setFieldsValue({
      category: [...(form.getFieldValue("category") || []), roleName],
    });
    setDropdownOpen(false);
  };
  const handleSelectLocation = (value: string) => {
    const selected = (locationFacets || []).find(
      (item) => item.locationId === value
    );
    if (selected) {
      setSelectedLocation({
        label: selected.description,
        value: selected.locationId,
      });
      form.setFieldValue("locationId", value);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ countryCode: "+91" }}
      onFinish={(values) => {
        onSubmit({
          ...values,
        });
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.form}>
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[{ required: true, message: "First name is required" }]}
        >
          <Input placeholder="First Name" />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[{ required: true, message: "Last name is required" }]}
        >
          <Input placeholder="Last Name" />
        </Form.Item>

        <Form.Item label="Location" name={"locationId"}>
          <Select
            showSearch
            searchValue={searchLocation}
            onSearch={setSearchLocation}
            placeholder="Search Location"
            value={selectedLocation?.value || undefined} // Ensure value is a string or undefined
            onChange={(value) => {
              handleSelectLocation(value);
            }}
            filterOption={(input, option) => true}
            notFoundContent={
              isFetching ? <CircularProgress /> : "No data available hey"
            }
          >
            {/* Show selected option at the top */}
            {selectedLocation && (
              <Select.Option
                key={selectedLocation.value}
                value={selectedLocation.value}
              >
                {selectedLocation.label}
              </Select.Option>
            )}

            {/* Render location options */}
            {(locationFacets || []).length > 0 ? (
              (locationFacets || [])
                .filter((item) => item.locationId !== selectedLocation?.value)
                .map((location) => (
                  <Select.Option
                    key={location.locationId}
                    value={location.locationId}
                  >
                    {location.description}
                  </Select.Option>
                ))
            ) : (
              <Select.Option disabled key="no-data" value="no-data">
                No data available
              </Select.Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: "Gender is Required" }]}
        >
          <Select>
            <Option value={GenderEnum.male}>{GenderEnum.male}</Option>
            <Option value={GenderEnum.female}>{GenderEnum.female}</Option>
            <Option value={GenderEnum.others}>{GenderEnum.others}</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Category is required" }]}
        >
          <Select
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            showSearch
            mode="multiple"
            placeholder="Select or create a role"
            optionFilterProp="children"
            allowClear
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space
                  style={{
                    padding: "0 8px 4px",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Input
                    placeholder="Enter new role"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newRole) {
                        e.stopPropagation();

                        e.preventDefault();
                        addNewCategories(newRole);
                      }
                    }}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    disabled={!newRole}
                    onClick={() => addNewCategories(newRole)}
                  >
                    Add Category
                  </Button>
                </Space>
              </>
            )}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {availableCategories.map((role) => (
              <Option key={role} value={role}>
                {role}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      <div className={styles.footer}>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default InfluencerForm;
